import { ROUTES } from "@src/common/constants";
import { AgentChatDrawer } from "@src/components/agent-chat/agent-chat-drawer";
import { registerAgentChatDrawerControls } from "@src/components/agent-chat/agent-chat-drawer-controls";
import { AgentChatFullscreen } from "@src/components/agent-chat/agent-chat-fullscreen";
import { registerAgentChatFullscreenControls } from "@src/components/agent-chat/agent-chat-fullscreen-controls";
import { getAgentChatSession } from "@src/components/agent-chat/agent-chat-session-store";
import {
	AgentChatProvider,
	useAgentChatContext,
} from "@src/components/agent-chat/hooks/agent-chat-context";
import { AppAgentProvider } from "@src/components/agent-chat/hooks/app-agent-context";
import {
	type BoardAgentContextValue,
	BoardAgentProvider,
} from "@src/components/agent-chat/hooks/board-agent-context";
import { LibraryAgentProvider } from "@src/components/agent-chat/hooks/library-agent-context";
import { useAppAgentActions } from "@src/components/agent-chat/hooks/use-app-agent-actions";
import { useLibraryAgentActions } from "@src/components/agent-chat/hooks/use-library-agent-actions";
import { useRegisteredBoardAgent } from "@src/components/agent-chat/hooks/use-registered-board-agent";
import { registerLibraryAgent } from "@src/components/agent-chat/library-agent-registry";
import {
	type RightDrawerTab,
	useRightDrawerTabStore,
} from "@src/components/agent-chat/right-drawer-tab-store";
import { useBoardEvents } from "@src/components/board/board-events-context";
import { useUserProfile } from "@src/components/user/user-context";
import {
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useLocation } from "react-router-dom";

const EMPTY_BOARD_AGENT: BoardAgentContextValue = {
	snapshot: null,
	actions: null,
};

export function AgentChatHost({ children }: { children?: ReactNode }) {
	const userProfile = useUserProfile();
	const [user] = userProfile.user;
	const libraryAgentActions = useLibraryAgentActions();
	const appAgentActions = useAppAgentActions();
	const registeredBoard = useRegisteredBoardAgent();
	const { emit, subscribe } = useBoardEvents();
	const location = useLocation();
	const isAskPage =
		location.pathname === ROUTES.ask ||
		location.pathname.toLowerCase().startsWith("/chat-");
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
	const [drawerEnterFromFullscreen, setDrawerEnterFromFullscreen] =
		useState(false);

	const libraryAgentValue = useMemo(
		() => ({
			actions: libraryAgentActions,
		}),
		[libraryAgentActions],
	);

	const appAgentValue = useMemo(
		() => ({
			actions: appAgentActions,
		}),
		[appAgentActions],
	);

	useEffect(() => {
		return registerLibraryAgent(libraryAgentValue);
	}, [libraryAgentValue]);

	const boardAgentValue = registeredBoard ?? EMPTY_BOARD_AGENT;

	const closeDrawer = useCallback(() => {
		setIsDrawerOpen(false);
		setDrawerEnterFromFullscreen(false);
		useRightDrawerTabStore.getState().setActiveTab("agent");
	}, []);

	const closeFullscreen = useCallback(() => {
		setIsFullscreenOpen(false);
	}, []);

	const closeAll = useCallback(() => {
		setIsDrawerOpen(false);
		setIsFullscreenOpen(false);
		setDrawerEnterFromFullscreen(false);
		useRightDrawerTabStore.getState().setActiveTab("agent");
	}, []);

	const pendingDrawerTabRef = useRef<RightDrawerTab | null>(null);

	const openDrawer = useCallback(
		(tab?: RightDrawerTab) => {
			if (isAskPage) {
				pendingDrawerTabRef.current = tab ?? "agent";
				return;
			}
			pendingDrawerTabRef.current = null;
			if (tab) {
				useRightDrawerTabStore.getState().setActiveTab(tab);
			}
			emit({ type: "close-all-card-drawers" });
			registeredBoard?.refreshSnapshot?.();
			setDrawerEnterFromFullscreen(false);
			setIsFullscreenOpen(false);
			setIsDrawerOpen(true);
		},
		[emit, isAskPage, registeredBoard],
	);

	const openFullscreen = useCallback(() => {
		if (isAskPage) {
			return;
		}
		emit({ type: "close-all-card-drawers" });
		registeredBoard?.refreshSnapshot?.();
		setIsDrawerOpen(false);
		setIsFullscreenOpen(true);
	}, [emit, isAskPage, registeredBoard]);

	const collapseToDrawer = useCallback(() => {
		registeredBoard?.refreshSnapshot?.();
		setDrawerEnterFromFullscreen(true);
		setIsFullscreenOpen(false);
		setIsDrawerOpen(true);
	}, [registeredBoard]);

	const toggleDrawer = useCallback(
		(tab?: RightDrawerTab) => {
			if (isAskPage) {
				return;
			}

			if (isFullscreenOpen) {
				if (tab) {
					useRightDrawerTabStore.getState().setActiveTab(tab);
				}
				collapseToDrawer();
				return;
			}

			const currentTab = useRightDrawerTabStore.getState().activeTab;

			if (isDrawerOpen) {
				if (tab && tab !== currentTab) {
					useRightDrawerTabStore.getState().setActiveTab(tab);
					return;
				}
				closeDrawer();
				return;
			}

			openDrawer(tab);
		},
		[
			closeDrawer,
			collapseToDrawer,
			isAskPage,
			isDrawerOpen,
			isFullscreenOpen,
			openDrawer,
		],
	);

	const wasAskPageRef = useRef(isAskPage);

	useEffect(() => {
		const wasAskPage = wasAskPageRef.current;
		wasAskPageRef.current = isAskPage;

		if (isAskPage) {
			setIsDrawerOpen(false);
			setIsFullscreenOpen(false);
			setDrawerEnterFromFullscreen(false);
			return;
		}

		if (!wasAskPage) {
			return;
		}

		const pendingTab = pendingDrawerTabRef.current;
		pendingDrawerTabRef.current = null;
		if (pendingTab) {
			useRightDrawerTabStore.getState().setActiveTab(pendingTab);
		}

		// Session store normalizes `submitting` → idle; messages or pending openDrawer cover transition.
		if (pendingTab || getAgentChatSession().messages.length > 0) {
			setIsDrawerOpen(true);
		}
	}, [isAskPage]);

	useEffect(() => {
		return registerAgentChatDrawerControls({
			open: openDrawer,
			close: closeAll,
			toggle: toggleDrawer,
			setTab: (tab) => useRightDrawerTabStore.getState().setActiveTab(tab),
		});
	}, [closeAll, openDrawer, toggleDrawer]);

	useEffect(() => {
		return registerAgentChatFullscreenControls({
			open: openFullscreen,
			close: closeAll,
		});
	}, [closeAll, openFullscreen]);

	useEffect(() => {
		const unsubscribeOpen = subscribe("open-agent-chat-drawer", (event) => {
			openDrawer(event.payload?.tab);
		});
		const unsubscribeClose = subscribe("close-agent-chat-drawer", () => {
			closeAll();
		});
		const unsubscribeToggle = subscribe("toggle-agent-chat-drawer", (event) => {
			toggleDrawer(event.payload?.tab);
		});

		return () => {
			unsubscribeOpen();
			unsubscribeClose();
			unsubscribeToggle();
		};
	}, [closeAll, openDrawer, subscribe, toggleDrawer]);

	return (
		<BoardAgentProvider value={boardAgentValue}>
			<LibraryAgentProvider value={libraryAgentValue}>
				<AppAgentProvider value={appAgentValue}>
					<AgentChatProvider>
						{children}
						{isAskPage || !user ? null : (
							<AgentChatSurfaces
								isDrawerOpen={isDrawerOpen}
								isFullscreenOpen={isFullscreenOpen}
								drawerEnterFromFullscreen={drawerEnterFromFullscreen}
								onDrawerClose={closeDrawer}
								onDrawerExpand={openFullscreen}
								onFullscreenClose={closeFullscreen}
								onFullscreenCollapse={collapseToDrawer}
							/>
						)}
					</AgentChatProvider>
				</AppAgentProvider>
			</LibraryAgentProvider>
		</BoardAgentProvider>
	);
}

function AgentChatSurfaces({
	isDrawerOpen,
	isFullscreenOpen,
	drawerEnterFromFullscreen,
	onDrawerClose,
	onDrawerExpand,
	onFullscreenClose,
	onFullscreenCollapse,
}: {
	isDrawerOpen: boolean;
	isFullscreenOpen: boolean;
	drawerEnterFromFullscreen: boolean;
	onDrawerClose: () => void;
	onDrawerExpand: () => void;
	onFullscreenClose: () => void;
	onFullscreenCollapse: () => void;
}) {
	const { abortStream } = useAgentChatContext();

	const handleDrawerClose = useCallback(() => {
		abortStream();
		onDrawerClose();
	}, [abortStream, onDrawerClose]);

	const handleFullscreenClose = useCallback(() => {
		abortStream();
		onFullscreenClose();
	}, [abortStream, onFullscreenClose]);

	return (
		<>
			<AgentChatDrawer
				isOpen={isDrawerOpen}
				enterFromFullscreen={drawerEnterFromFullscreen}
				onClose={handleDrawerClose}
				onExpand={onDrawerExpand}
			/>
			<AgentChatFullscreen
				isOpen={isFullscreenOpen}
				onClose={handleFullscreenClose}
				onCollapse={onFullscreenCollapse}
			/>
		</>
	);
}
